import React, { useRef, useState } from 'react'
import RightArrowSvg from 'components/RightArrowSvg'
import { StyleUtil } from 'utils/StyleUtil'
import { ToastUtil } from 'utils/ToastUtil'
import excelSvg from '../images/excel.svg'
import Spinner from './Spinner'
import { FileObject } from 'apis/entities/file.entity'
import { useCompositeAuth } from 'providers/CompositeAuthProvider'
import Svg, { Icon } from 'components/Svg'
import { useArchitectureApi } from 'providers/ArchitectureApiProvider'
import Emitter, { Events } from 'core/emitter'
import close2 from 'images/close2.svg'

interface Props {
  intakeId: string
  placeholder: string
  onClose: (refresh: boolean) => void
}

export default function WidgetArchUpload({
  intakeId,
  placeholder,
  onClose,
}: Props): JSX.Element {
  const { user } = useCompositeAuth()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const refInput = useRef<HTMLInputElement | null>(null)
  const [files, setFiles] = useState<FileObject[]>([])
  const refOverlay = useRef<HTMLDivElement | null>(null)
  const { uploadIntakeExcel } = useArchitectureApi()
  const refIsUploaded = useRef<boolean>(false)
  // for drag and drop
  let counter = 0

  const addFiles = (newFiles: File[]) => {
    const tmpFiles = newFiles.map((file) => {
      return { objectURL: URL.createObjectURL(file), file: file }
    })
    setFiles(files.concat(tmpFiles))
  }

  const hasFiles = (e: React.DragEvent<HTMLDivElement>) => {
    const types = e.dataTransfer.types
    return types.indexOf('Files') > -1
  }

  const onSelect = () => {
    refInput.current?.click()
  }

  const onInputFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files || []

    // check the file extension is xls or xlsx
    for (const file of files) {
      const ext = file.name.split('.').pop()
      if (ext !== 'xls' && ext !== 'xlsx') {
        ToastUtil.warning('Please select .xls or .xlsx file')
        return
      }
    }

    const newFiles: File[] = []
    for (const file of files) {
      newFiles.push(file)
    }
    addFiles(newFiles)
    // reset selected files
    refInput.current!.value = ''
  }

  const onDropHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    // console.log(e.dataTransfer.files)
    const newFiles: File[] = []
    for (const file of e.dataTransfer.files) {
      newFiles.push(file)
      refOverlay.current?.classList.remove('draggedover')
      counter = 0
    }
    addFiles(newFiles)
  }

  const onDragEnterHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!hasFiles(e)) {
      return
    }
    ++counter && refOverlay.current?.classList.add('draggedover')
  }

  const onDragLeaveHandler = (e: React.DragEvent<HTMLDivElement>) => {
    1 > --counter && refOverlay.current?.classList.remove('draggedover')
  }

  const onDragOverHandler = (e: React.DragEvent<HTMLDivElement>) => {
    if (hasFiles(e)) {
      e.stopPropagation()
      e.preventDefault()
    }
  }

  const onRemoveFile = (index: number) => {
    const tmpFiles = [...files]
    tmpFiles.splice(index, 1)
    setFiles(tmpFiles)
  }

  const onUpload = async () => {
    if (files.length === 0) {
      ToastUtil.warning('Please select file')
      return
    }

    setIsLoading(true)
    for (let i = 0; i < files.length; i++) {
      try {
        const file = files[i].file
        let formData = new FormData()
        formData.append('file', file)
        formData.append('auth0Id', user?.auth0Id ?? user?.id ?? '')
        formData.append('username', user?.username || '')
        formData.append('firstName', user?.firstName || '')
        formData.append('email', user?.email || '')
        // for new login
        formData.append('userId', user?.id || '')

        await uploadIntakeExcel(intakeId, file)
        ToastUtil.success('Uploaded successfully')
        refIsUploaded.current = true
        // auto close
        onClose(true)
        // update the client
        Emitter.emit(Events.ReloadArchClient, {})
      } catch (err) {
        console.error(err)
        ToastUtil.error('Upload failed')
      }
    }
    setIsLoading(false)
    // reset files
    setFiles([])
  }

  return (
    <div
      className="absolute z-[999] flex h-full w-full items-center justify-center bg-[#D5D5DBAA]"
      onClick={
        // click outside the widget to close
        (e) => {
          if (e.target === e.currentTarget) {
            onClose(false)
          }
        }
      }
    >
      <article
        style={{
          width: 560,
          margin: '0 auto',
          paddingTop: 24,
          paddingLeft: 36,
          paddingRight: 36,
          paddingBottom: 36,
          boxShadow:
            '0px 570px 160px 0px rgba(0, 0, 0, 0.00), 0px 365px 146px 0px rgba(0, 0, 0, 0.01), 0px 205px 123px 0px rgba(0, 0, 0, 0.05), 0px 91px 91px 0px rgba(0, 0, 0, 0.09), 0px 23px 50px 0px rgba(0, 0, 0, 0.10)',
        }}
        aria-label="File Upload Modal"
        className="relative flex flex-col bg-white"
        onDrop={onDropHandler}
        onDragOver={onDragOverHandler}
        onDragLeave={onDragLeaveHandler}
        onDragEnter={onDragEnterHandler}
      >
        <div
          style={{
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: 24,
          }}
          onClick={() => onClose(refIsUploaded.current)}
        >
          <img src={close2} alt="Close" style={{ height: 24 }} />
        </div>

        <section className="mx-auto flex h-full w-full flex-col overflow-hidden">
          <header className="border-1 flex h-[270px] flex-col items-center justify-center border border-gray-400">
            {files.length === 0 && (
              <>
                <p
                  className="flex flex-wrap justify-center"
                  style={{
                    fontWeight: 700,
                    fontSize: 20,
                  }}
                >
                  <span>Drag file here to upload </span>
                </p>
                <input
                  data-testid="hidden-input"
                  id="hidden-input"
                  title="hidden-input"
                  type="file"
                  multiple={false}
                  className="hidden"
                  ref={refInput}
                  onChange={onInputFiles}
                />
                <button
                  data-testid="btn-select"
                  type="button"
                  id="btn-select"
                  onClick={() => onSelect()}
                  className={`${StyleUtil.buttonPrimaryUnsw} mb-0 mt-[60px]`}
                  disabled={isLoading}
                >
                  <span className="pl-2 pr-2">Select file</span>
                </button>
              </>
            )}
          </header>
          <div className="pt-6">Preview</div>
          {files.length > 0 ? (
            <div
              style={{
                marginTop: 6,
                marginBottom: 24,
              }}
            >
              <img src={excelSvg} alt="spreadsheet" />
              <span
                style={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: 16,
                  fontWeight: 700,
                  verticalAlign: 'top',
                }}
              >
                {files[0].file.name}
              </span>
              <div className="cursor-pointer" onClick={() => onRemoveFile(0)}>
                <Svg icon={Icon.Close} />
              </div>
            </div>
          ) : (
            <div style={{ height: 50 }}></div>
          )}
        </section>
        {isLoading && <Spinner />}
        <footer className="flex justify-center">
          {!isLoading && (
            <button
              data-testid="btn-upload"
              type="button"
              id="btn-upload"
              onClick={onUpload}
              disabled={isLoading}
              className={StyleUtil.buttonPrimaryUnsw + ' !w-64'}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <span className="pl-2 pr-2">Upload</span>
              <RightArrowSvg className="right-arrow" />
            </button>
          )}
        </footer>
      </article>
    </div>
  )
}
